import type {
  CampaignContentMetadata,
  QuestAPIResponse,
} from "@/app/games/(details)/types";
import type { Content } from "@prismicio/client";
import type { QuestKey } from "@ui-kit/QuestList";

export type QuestReward = {
  amount: number;
  type: string;
};

export type QuestCompletion = {
  completion_count: number;
  external_id: string;
  game_id: string;
  latest_completion_time: string;
  latest_rewards: QuestReward[];
  total_rewards: QuestReward[];
};

export type QuestCompletions = Record<string, QuestCompletion>;

export const QUEST_TYPES = {
  JOIN_DISCORD_SERVER: "join_discord_server",
  FOLLOW_X_ACCOUNT: "follow_x_account",
  POST_X_TWEET: "post_x_tweet",
  RETWEET_AND_LIKE: "retweet_and_like",
  JOIN_TELEGRAM_CHAT: "join_telegram_chat",
  APP_STORE_IMAGE_VALIDATION: "app_store_image_validation",
  TRAILER: "trailer",
  WEBSITE: "website",
  STEAM_WISHLIST: "steam_wishlist",
  CAMPAIGN_REFERER: "campaign_referrer",
  CAMPAIGN_REFEREE: "campaign_referee",
  SOCIAL_ENGAGEMENT: "social_engagement",
  WISHLIST: "wishlist",
  IN_GAME: "in_game",
  NO_VALIDATE: "no_validate",
} as const;

export type CampaignQuestType = (typeof QUEST_TYPES)[keyof typeof QUEST_TYPES];

export const SOCIAL_QUEST_TYPES = new Set<string>([
  QUEST_TYPES.JOIN_DISCORD_SERVER,
  QUEST_TYPES.FOLLOW_X_ACCOUNT,
  QUEST_TYPES.POST_X_TWEET,
  QUEST_TYPES.RETWEET_AND_LIKE,
  QUEST_TYPES.JOIN_TELEGRAM_CHAT,
  QUEST_TYPES.APP_STORE_IMAGE_VALIDATION,
  QUEST_TYPES.TRAILER,
  QUEST_TYPES.WEBSITE,
  QUEST_TYPES.STEAM_WISHLIST,
  QUEST_TYPES.CAMPAIGN_REFERER,
  QUEST_TYPES.CAMPAIGN_REFEREE,
  QUEST_TYPES.SOCIAL_ENGAGEMENT,
  QUEST_TYPES.WISHLIST,
]);

export const IN_GAME_QUEST_TYPES = new Set<string>([
  QUEST_TYPES.IN_GAME,
  QUEST_TYPES.NO_VALIDATE,
]);

export type CampaignType = "creator" | "standard";

export type Campaign = {
  endDate: Date;
  externalId: string;
  gameId: string;
  id: number;
  name: string;
  startDate: Date;
  type: CampaignType;
  contentMetadata?: CampaignContentMetadata;
};

export type CampaignHistory = Campaign & {
  prizeDistributed: boolean;
};

export type CampaignQuest = {
  campaignPoints: number;
  externalId: string;
  gameId: string;
  gems: number;
  id: number;
  instantDistribution: boolean;
  name: string;
  perpetualRewardsEligible: boolean;
  refreshPeriodHours: number | null;
  type: CampaignQuestType | string;
  validFrom: string | null;
  validTo: string | null;
  participantCount: number | null;
  categories: string[];
  validationType?: "pre-registration" | "download";
  referralBonusPoints?: number;
};

type Platform = {
  apple_app_store?: string;
  epic?: string;
  google_play?: string;
  microsoft_store?: string;
  steam?: string;
  web_url?: string;
};
// API Response interface for /v1/quests/games-with-quests
interface GameWithQuestsAPIData {
  game_background_url: string;
  game_landing_page_url?: string;
  game_description?: string;
  game_genres: string[];
  game_id: string;
  game_logo: string;
  game_name: string;
  game_slug: string;
  platforms: Platform[];
  quests: QuestAPIResponse[];
}

export interface GamesWithQuestsAPIResponse {
  result: GameWithQuestsAPIData[];
}

export interface GameWithQuests {
  gameLandingPageUrl?: string;
  gameBackgroundUrl: string;
  gameDescription?: string;
  gameGenres: string[];
  gameId: string;
  gameLogo: string;
  gameName: string;
  gameSlug: string;
  platforms: Platform[];
  quests: CampaignQuestState[];
}

export interface GameGroup {
  gameId: string;
  gameName: string;
  gameIcon?: string;
  gameSlug?: string;
  gameLandingPageUrl?: string;
  gameGenres: string[];
  quests: CampaignQuestState[];
  featured: boolean;
}

export type PrismicQuest = {
  externalId: string;
  onClickUrl: string;
  caption?: string;
};

export type CampaignContent = {
  campaign: Campaign;
  campaignDoc: Content.PaasCampaignDocument;
};

export interface XQuestData {
  type: "x";
  likeRetweetId?: string;
  tweetText?: string;
  tweetUsername?: string;
  tweetRequiresImage?: boolean;
  tweetCaption?: string;
  tweetDraftText?: string;
  tweetPlaceholder?: string;
  tweetSubstring?: string;
}

// Will union the other types, but for now will be XQuestData
export type PrimitiveQuestData = XQuestData;

export type CampaignQuestState = CampaignQuest & {
  weeklyPoints?: number;
  linkUrl?: string;
  completed: boolean;
  completionCount?: number;
  completedToday: boolean;
  caption?: string;
  latestCompletion?: string;
  primitiveData?: PrimitiveQuestData;
};

export type PrismicQuestGroup = {
  name: string;
  prismicQuests: PrismicQuest[];
};

export type CampaignQuestGroup = {
  quests: CampaignQuestState[];
  name: string;
};

export type QuestViewData = {
  campaignQuestGroups: CampaignQuestGroup[];
  visibleCampaignContent?: CampaignContent;
  campaignHistory: CampaignHistory[];
  xQuestData: Record<string, Content.XQuestDataDocument>;
  refereeQuest?: CampaignQuest;
};

export const mapCampaignQuestType = (
  type: CampaignQuestType | string,
): QuestKey => {
  switch (type) {
    case "join_discord_server":
      return "discord-v2";
    case "follow_x_account":
      return "x-v2";
    case "post_x_tweet":
      return "x-post-tweet-validation";
    case "retweet_and_like":
      return "x-like-and-retweet-validation";
    case "join_telegram_chat":
      return "telegram-v2";
    case "in_game":
      return "in-game";
    case "app_store_image_validation":
      return "app-store-image-validation";
    case "steam_wishlist":
      return "steam";
    case "campaign_referrer":
    case "campaign_referee":
      return "leaderboard-referral";
    default:
      return "website";
  }
};
